import React, {useMemo} from 'react';
import cx from 'classnames';
import SvgIcon, {SvgIconProps} from '@material-ui/core/SvgIcon';
import {svgs, SvgProps} from '../../constants';
import {isSrc} from '../../utils/PropUtils';
import useStyles from './Icon.styles';

export interface IconProps extends Omit<SvgIconProps, 'ref'> {
    ariaLabel?: string;
    icon: keyof typeof svgs | SvgProps;
    onClick?: (e?) => void;
    flip?: 'horizontal';
}

const Icon = React.forwardRef<HTMLSpanElement, IconProps>((props, ref) => {
    const styles = useStyles();

    const {
        ariaLabel,
        className,
        onClick = () => {},
        icon,
        viewBox,
        children,
        flip,
        ...svgProps
    } = props;

    if (isSrc(icon)) {
        return (
            <span
                className={cx(styles.wrapper, className, styles.bgImage)}
                style={{backgroundImage: `url(${icon})`}}
                onClick={onClick}
                aria-label={ariaLabel}
            />
        );
    }

    const {_children, _viewBox} = useMemo(() => {
        let svgProps: SvgProps;

        if (icon) {
            svgProps = typeof icon === 'string' ? svgs[icon] : icon;
            if (svgProps) {
                return {
                    _viewBox: svgProps.viewBox,
                    _children: svgProps.data
                };
            }
        }

        return {_viewBox: viewBox, _children: children};
    }, [icon]);

    const wrapperClasses = cx(styles.wrapper, className);
    const svgClasses = cx({
        [styles[flip]]: !!flip
    });

    return (
        <span ref={ref} className={wrapperClasses} onClick={onClick} aria-label={ariaLabel}>
            <SvgIcon className={svgClasses} viewBox={_viewBox} {...svgProps}>
                {_children}
            </SvgIcon>
        </span>
    );
});

export default Icon;
